// Phase 31.10: Cross-page tx-saved synchronization.
//
// Old approach: every page mounted `window.addEventListener('finance:tx-saved',
// ...)` and AddTransactionModal dispatched the event after each save. That worked
// but was fragile (leaks if listener not removed on unmount; no version state;
// duplicates if the same page mounts the listener twice).
//
// New approach: a tiny in-memory version store with subscribe/notify + a
// React hook. Pages register `useTxSync(callback)` to be re-invoked when any
// tx change happens. We also keep the legacy window event so unmigrated
// callers still work during the transition.
let version = 0;
const listeners = new Set();

export function notifyTxSaved(detail) {
  version += 1;
  for (const cb of listeners) {
    try { cb({ version, detail }); } catch (e) { /* keep going */ }
  }
  // Legacy bridge — keep firing the window event until all callers migrate.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('finance:tx-saved', { detail }));
  }
}

export function getTxVersion() { return version; }

export function subscribeTxSync(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// React hook. Re-runs `callback` whenever the tx version bumps.
// `callback` is wrapped in `useCallback` so referential equality is preserved.
import { useEffect } from 'react';
export default function useTxSync(callback, deps = []) {
  useEffect(() => {
    const unsub = subscribeTxSync(() => callback());
    // Also pick up the legacy event so old + new callers stay in sync.
    const onLegacy = () => callback();
    window.addEventListener('finance:tx-saved', onLegacy);
    return () => {
      unsub();
      window.removeEventListener('finance:tx-saved', onLegacy);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}